/*************************************************************
 * Tracks and saves the progress of a scorm course
 * 
 * To view at runtime check console for:  window.scormTracker
 *************************************************************/

import moment, { duration } from 'moment';
import { ScormRuntime12 } from './ScormRuntime12';  
import { ScormRuntime2004 } from './ScormRuntime2004'; 
import { VideoRuntime } from './VideoRuntime'; 
import { patchAssignment } from '../services/CourseService/CourseService';

const SAVE_INTERVAL = 2000;
const WINDOW_CLOSE_INTERVAL = 200;
const IN_PROGRESS = 'IN_PROGRESS', COMPLETED = 'COMPLETED', ASSIGNED = 'ASSIGNED';

/*************************************************************
 * Entry point for tracking a scorm course. This acts like a 
 * singleton so only one instance of trackScorm ever exists.
 *************************************************************/

export const trackScorm = ( course, lesson, studentName, studentId, token ) => {
    if ( !window.scormTracker ) {
        window.scormTracker = new ScormTracker( { 
            debug: false, debugAsNewCourse: false } );
    }
    
    window.scormTracker.startTracking( course, lesson, studentName, studentId, token );
};

export const openWindow = ( url, closeCallback ) => {
    if ( window.scormTracker ) {
        return window.scormTracker.openWindow( url, closeCallback );
    } else {
        console.error( 'Not tracking scorm' );
    }
};

/*************************************************************
 * The actual tracker
 *************************************************************/

class ScormTracker {
    debug = false;
    debugAsNewCourse = false;
    course = {};
    courseId = '';
    lessonId = '';
    lesson = {};
    studentId = '';
    studentName = '';
    saveCache = null;
    scormVersion = '';
    token = '';
    isTracking = false;
    saveTimer = undefined;
    windowCloseTimer = undefined;
    lessonStatus = '';
    courseWindow = undefined; 
    closeCallback = undefined;
    courseStartTime = undefined;
    previousCourseTimeSpent = undefined;

    constructor( { debug = false, debugAsNewCourse = true } ) {
        this.debug = debug;
        this.debugAsNewCourse = debugAsNewCourse;
    }

    /********************************
     * Starts tracking and sets all 
     * listeners and vars
     ********************************/

    startTracking( course, lesson, studentName, studentId, token ) {
        /********************************
         * Reset vars so nothing remains
         * from previously tracked course 
         ********************************/

        if ( !course || !lesson ) { 
            return;
        }

        if ( this.isTracking ) {
            this.stopTracking();
        }

        if ( this.courseWindow ) {
            this.courseWindow.close();
            this.courseWindow = undefined;
        }

        if ( this.windowCloseTimer ) {
            clearInterval( this.windowCloseTimer );
            this.windowCloseTimer = undefined;
        }

        /********************************
         * Setup vars and start tracking  
         ********************************/

        this.debug && console.log( 'Starting tracking' );
        
        this.token = token;
        this.course = course;
        this.courseId = course.id;
        this.lessonId = lesson.id;
        this.studentId = studentId;
        this.studentName = studentName;
        this.courseStartTime = moment();

        if ( this.debugAsNewCourse ) {
            this.lesson = { ...lesson, assignment: undefined };
        } else {
            this.lesson = { ...lesson };
            this.lessonStatus = lesson.assignment?.status || IN_PROGRESS;
        }

        if ( !this.lesson.assignment ) {
            this.setInitialAssignment();
        }

        if ( this.lesson?.assignment?.scormState?.BIW_timeSpent ) {
            this.previousCourseTimeSpent = this.lesson?.assignment?.scormState?.BIW_timeSpent;
            console.log( 'TIME Previous ', this.previousCourseTimeSpent );
        }

        this.addScormListeners();
        this.startSaveTimer();
        this.isTracking = true;
    }
    
    /********************************
     * Stops tracking and resets all 
     * listeners and vars
     ********************************/

    stopTracking() {
        this.debug && console.log( 'Stoping tracking' );
        
        this.stopSaveTimer();
        this.removeScormListeners();
        this.course = {};
        this.lesson = {};
        this.studentId = '';
        this.studentName = '';
        this.saveCache = null;
        this.scormVersion = '';
        this.isTracking = false;
        this.courseStartTime = undefined; 
        this.previousCourseTimeSpent = undefined; 
    }

    /********************************
     * Handler for opening scorm in 
     * a new window
     ********************************/

    openWindow( url, closeCallback ) {
        this.debug && console.log( 'Opening course in new window' );
        
        this.closeCallback = closeCallback;

        this.courseWindow = window.open(
            url,
            'CourseWindow',
            [ 
                'resizable=1',
                'width=800',
                'height=800',
                'left=0',
                'top=0',
            ].join( ',' )
        );

        this.windowCloseTimer = setInterval( () => {
            if ( this.courseWindow?.closed ) {
                if ( this.closeCallback ) {
                    this.closeCallback();
                }
                clearInterval( this.windowCloseTimer );
                this.windowCloseTimer = undefined;
            }
        }, WINDOW_CLOSE_INTERVAL );

        return this.courseWindow;
    }

    /********************************
     * Save and save timer handling
     ********************************/
    
    startSaveTimer() {
        this.debug && console.log( 'Starting timer' );

        if ( this.saveTimer ) {
            this.stopSaveTimer();
        }

        this.saveTimer = setInterval( this.save.bind( this ), SAVE_INTERVAL );
    }

    stopSaveTimer() {
        this.debug && console.log( 'Stoping timer' );
    
        if ( this.saveTimer ) {
            clearInterval( this.saveTimer );
            this.saveTimer = undefined;
        }
    }

    async save() {
        if ( this.saveCache !== null && this.lessonStatus !== COMPLETED ) {
            const model = { ...this.saveCache.model };

            if ( model.type === 'VIDEO' ) {            
                let status = ASSIGNED;

                if ( model.cmi.finished ) {
                    status = COMPLETED;
                } else if ( model.cmi.started ) {
                    status = IN_PROGRESS;
                } 
                
                let timeSpent = Math.trunc( model.cmi.position );

                this.saveCache = null;
                this.lessonStatus = status;
                
                this.debug && console.log( 'Saving video progress with: ', model, status, timeSpent );

                try {
                    const inputAssigned = {
                        input: { 
                            learningObjectID: this.lessonId,
                            courseID: this.courseId,
                            scormState: model,
                            status: status,
                            score: 0,
                            timeSpent: timeSpent
                        }
                    };

                    patchAssignment( this.token, inputAssigned )
                        .then( ( response ) => {
                            if ( !response.success ) {
                                 console.log( 'Error patching assignment' );
                            }
                        } 
                    );
                }
                catch ( error ) {
                    console.error( error ); 
                }
            } else {
                const status = this.saveCache.status;
                let timeSpent = this.getTimeSpentInSeconds( model );
                
                if ( !timeSpent || timeSpent === 0 ) {
                    console.log( 'TIME: IS ZERO' );

                    const diffInMilliseconds = moment().diff( this.courseStartTime );
                    const diffInSeconds =  Math.round( moment.duration( diffInMilliseconds ).asSeconds() );
                    timeSpent = diffInSeconds;
                    
                    console.log( 'TIME: ', timeSpent );

                    if ( this.previousCourseTimeSpent ) {
                        console.log( 'TIME: ADDING PREVIOUS', this.previousCourseTimeSpent );

                        timeSpent += this.previousCourseTimeSpent;
                    }

                    console.log( 'TIME PLUS PREVIOUS: ', timeSpent  );
                    model.BIW_timeSpent = timeSpent;
                }
                
                const score = this.getScore( model );

                this.saveCache = null;
                this.lessonStatus = status;
                
                console.log( 'Saving Scorm progress with: ', model, status, timeSpent, score );
                
                try {

                    const inputAssigned = {
                        input: { 
                            learningObjectID: this.lessonId,
                            courseID: this.courseId,
                            scormState: model,
                            status: status,
                            score: status === COMPLETED ? score : undefined,
                            timeSpent: status === COMPLETED ? timeSpent : undefined
                        }
                    };

                    patchAssignment( this.token, inputAssigned )
                        .then( ( response ) => {
                            if ( !response.success ) {
                                console.log( 'Error patching assignment' );
                            }
                        } 
                    );
                }
                catch ( error ) {
                    console.error( error ); 
                }
            }
        }
    }

    /********************************
     * Scorm listener handling
     ********************************/

    addScormListeners() {
        this.debug && console.log( 'Adding listeners' );

        window.API_1484_11 = new ScormRuntime2004( { 
            CMIModel: JSON.parse( JSON.stringify( this.lesson?.assignment?.scormState || {} ) ), 
            debug: true,
            studentId: this.studentId,
            studentName: this.studentName,
            eventCallbacks: {
                initialize: () => {
                    this.scormVersion = '2004';
                    this.debug && console.log( 'Tracking with runtime for scorm 2004' );
                },
                change: ( event, model ) => { 
                    console.log( 'Change 2004: ', event, model );
                    this.addToSaveCache( model );
                },
                commit: ( event, model ) => { 
                    console.log( 'Commit 2004: ', event, model );
                    this.addToSaveCache( model );
                }
            }
        } );
        
        window.API = new ScormRuntime12( { 
            CMIModel: JSON.parse( JSON.stringify( this.lesson?.assignment?.scormState || {} ) ), 
            debug: true,
            studentId: this.studentId,
            studentName: this.studentName,
            eventCallbacks: {
                initialize: () => {
                    this.scormVersion = '1.2';
                    this.debug && console.log( 'Tracking with runtime for scorm 1.2' );
                },
                change: ( event, model ) => { 
                    console.log( 'Change 12: ', event, model );
                    this.addToSaveCache( model );
                },
                commit: ( event, model ) => { 
                    console.log( 'Commit 12: ', event, model );
                    this.addToSaveCache( model );
                }
            }
        } );

        window.VIDEO = new VideoRuntime( {
            CMIModel: JSON.parse( JSON.stringify( this.lesson?.assignment?.scormState || {} ) ), 
            debug: false,
            studentId: this.studentId,
            studentName: this.studentName,
            eventCallbacks: {
                change: ( event, model ) => {
                    this.debug && console.log( 'Tracking with runtime for video' );
                    
                    this.scormVersion = 'VIDEO';

                    this.addToSaveCache( model );
                },
                start: ( event, model ) => {
                    this.addToSaveCache( model );
                },
                finish: ( event, model ) => { 
                    this.addToSaveCache( model );
                }
            }
        } );
    }
    
    removeScormListeners() {
        this.debug && console.log( 'Removing listeners' );
        
        delete window.API_1484_11;
        delete window.API;
        delete window.VIDEO;
    }
    
    /********************************
     * Utility functions
     ********************************/

    addToSaveCache( model ) {
        const status = this.getCourseStatus( model );

        this.saveCache = { model, status };
    }

    getCourseStatus( model ) {
        const statusForScorm2004 = model?.cmi?.completion_status;

        if ( statusForScorm2004 ) {
            if ( statusForScorm2004 === 'completed' ) { 
                return COMPLETED ;
            } else {
                if ( statusForScorm2004 === 'incomplete' || statusForScorm2004 === 'not attempted' ) {
                    return IN_PROGRESS;
                } else {
                    return ASSIGNED;
                }
            }
        }  

        const statusForScorm12 = model?.cmi?.core?.lesson_status;

        if ( statusForScorm12 ) {
            if ( statusForScorm12 === 'completed' || statusForScorm12 === 'passed' ) {
                return COMPLETED;
              } else {
                  if ( statusForScorm12 === 'failed' || statusForScorm12 === 'incomplete' ) { 
                      return IN_PROGRESS ;
                  } else {
                      return ASSIGNED;
                  }
              }
        }

        return IN_PROGRESS;
    }

    getTimeSpentInSeconds( model ) { 
        let sessionTime = model?.cmi?.core?.session_time;
        let totalTime = model?.cmi?.core?.total_time;
    
        if ( !sessionTime && !totalTime ) {
            sessionTime = model?.cmi?.session_time;
            totalTime = model?.cmi?.total_time;
        }
    
        if ( !sessionTime && !totalTime ) {
            return undefined;
        }

        if ( sessionTime.startsWith( 'P' ) ) {  
            const regex = /^P(?:\d+Y|Y)?(?:\d+M|M)?(?:\d+D|D)?(?:T(\d+H|H)?(\d+M|M)?(\d+(\.\d{1,2})?S|S)?)?$/;
        
            let matches;
        
            if ( ( matches = regex.exec( sessionTime ) ) !== null ) {
                let hours = 0, min = 0, sec = 0; 
            
                matches.forEach( ( match ) => {
                    if ( match ) {
                        // Set hours
                        if ( match.endsWith( 'H' ) ) {
                            hours = parseInt( match.slice( 0, -1 ) );
                            if ( isNaN( hours ) ) {
                                hours = 0;
                            }
                        }
                    
                        // Set minuetes
                        if ( match.endsWith( 'M' ) ) { 
                            min = parseInt( match.slice( 0, -1 ) );
                            if ( isNaN( min ) ) {
                                min = 0;
                            }
                        }
                    
                            // Set seconds
                        if ( match.endsWith( 'S' ) ) { 
                            sec = parseInt( match.slice( 0, -1 ) );
                            if ( isNaN( sec ) ) {
                                sec = 0;
                            }
                        }
                    }
                } );

                let time = ( hours * 3600 ) + ( min * 60 ) + sec;
                if ( time < 1 ) {
                    time = 1;
                }

                return time;
            }
        } else {
            let total = 0;

            if ( sessionTime ) {
                const time = Math.trunc( this.formatDuration( sessionTime ) );
                total += time;
            }
            if ( totalTime ) {
                const time = Math.trunc( this.formatDuration( totalTime ) );
                total += time;
            }
        
            return total;    
        }
    
        return undefined;
    }

    formatDuration( time ) {
        const moment = duration( time );
        return Math.floor( moment.asSeconds() );     
    }

    getScore( model ) {
        let rawScore;

        if ( model.cmi?.core?.score ) {
            const score = model.cmi.core.score;
            rawScore = score.raw;
        } else {
            if ( model.cmi?.score ) {
                const score = model.cmi.score;
                rawScore = score.raw;
            }
        }

        if ( rawScore ) {
            const result = parseInt( rawScore, 10 );

            if ( !isNaN( result ) ) {
                this.debug && console.log( 'Score: ', result );
                return result;
            }
        }

        this.debug && console.log( 'Score: NOSCORE' );
        
        return undefined;
    }

    setInitialAssignment() {
        this.debug && console.log( 'Creating initial assignment' );
            
        try {
            const inputAssigned = {
                input: { 
                    learningObjectID: this.lessonId,
                    courseID: this.courseId,
                    status: ASSIGNED
                }
            };

            patchAssignment( this.token, inputAssigned )
                .then( ( response ) => {
                    if ( !response.success ) {
                        console.log( 'Error patching assignment' );
                    }
                } 
            );
        }
        catch ( error ) {
            console.error( error ); 
        }
    }
}
